/**
 * RecipeIngredientRow Component
 *
 * Single ingredient row with quantity input for recipe editing.
 * Shows item name, editable quantity, unit, and remove button.
 */

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Props:
 * - item: { id, name, unit_of_measure }
 * - quantity: number
 * - onQuantityChange: (qty: number) => void
 * - onRemove: () => void
 * - disabled: boolean
 * - perUnitLabel: string (e.g., "per bag", "per jar", "per block")
 */
export default function RecipeIngredientRow({
  item,
  quantity,
  onQuantityChange,
  onRemove,
  disabled = false,
  perUnitLabel = 'per bag'
}) {
  const handleQuantityChange = (e) => {
    const value = e.target.value

    // Allow empty for clearing
    if (value === '') {
      onQuantityChange(0)
      return
    }

    // Parse as number
    const numValue = parseFloat(value)
    if (!isNaN(numValue) && numValue >= 0) {
      onQuantityChange(numValue)
    }
  }

  // Format unit for display
  const unit = item.unit_of_measure || ''

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 rounded-md border bg-muted/30",
      disabled && "opacity-60"
    )}>
      {/* Remove button */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        onClick={onRemove}
        disabled={disabled}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Remove {item.name}</span>
      </Button>

      {/* Item name */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {item.name}
        </p>
      </div>

      {/* Quantity input */}
      <div className="flex items-center gap-2 shrink-0">
        <Input
          type="number"
          value={quantity || ''}
          onChange={handleQuantityChange}
          disabled={disabled}
          className="w-20 h-8 text-right"
          min="0"
          step="any"
          placeholder="0"
        />
        <span className="text-sm text-muted-foreground w-12">
          {unit}
        </span>
      </div>

      {/* Per unit label */}
      <span className="text-xs text-muted-foreground shrink-0">
        {perUnitLabel}
      </span>
    </div>
  )
}
